/**
 * The permission ids (D14; PLAN §11): users → groups → `PermissionIdWire[]`, each
 * group with a scope. The single list every route, authorizer, group editor
 * and offline check draws from — a plain exported const, so the cloud
 * foundations (tenancy, groups, the authorizer) and the rundown API import the
 * same ids.
 *
 * Add an id here, in the same change as the first route or check that needs
 * it. Ids are never renamed: they are stored in every group record, cloud and
 * node alike.
 */
import { z } from 'zod';

export const PERMISSION_IDS = [
  /** Manage the tenant: users, groups, sites, studios. */
  'tenant.admin',
  /** See billing and usage. */
  'billing.view',
  /** Read rundowns and playlists and their rows. */
  'rundown.read',
  /** Create, edit, reorder and (soft) delete rundowns, playlists and their rows. */
  'rundown.edit',
  /** Take, set next, adlib; confirm on-air updates of live-editable fields (D19). */
  'playout.take',
  /** Activate, deactivate and reset a playlist. */
  'playout.activate',
  /** Launch a production on an on-prem node. */
  'production.launch.onprem',
  /** Launch a cloud production (D10). */
  'production.launch.cloud',
  /** Switch a production (or a whole node) between main and backup (D9). */
  'node.switchover',
  /** Configure studios: devices, mappings, piece types. */
  'device.configure',
  /** Break-glass offline login on a node while it cannot reach the cloud (D20). */
  'local.login',
  /** Cross-tenant vendor operations dashboard (D27). */
  'vendor.ops',
  /** Cross-tenant vendor administration: create and list tenants (D13, D27). */
  'vendor.admin',
] as const;

/** `vendor.*` permissions are grantable only inside the vendor's own tenant (D27). */
export function isVendorPermission(permission: PermissionIdWire): boolean {
  return permission.startsWith('vendor.');
}

export const permissionIdWireSchema = z.enum(PERMISSION_IDS);
export type PermissionIdWire = z.infer<typeof permissionIdWireSchema>;

/** Whether `value` is a known permission id. */
export function isPermissionId(value: string): value is PermissionIdWire {
  return permissionIdWireSchema.safeParse(value).success;
}
